import { type ReactNode, useEffect, useRef } from "react";

import { dropTargetForExternal } from "@atlaskit/pragmatic-drag-and-drop/external/adapter";
import { containsFiles } from "@atlaskit/pragmatic-drag-and-drop/external/file";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { DataRoomFileTreeCell } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/DataRoomFileTreeCell";
import { TrackingTag } from "@vega-modules/scenes/CoInvestmentsGP/components/TrackingTable/components/TrackingTag";

import type { TreeNode } from "./types";

type FileTreeRowProps = {
  node: TreeNode;
  depth: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  addItemMenu?: ReactNode;
  onOpenFile?: (node: TreeNode) => void;
  onOpenPdf?: (node: TreeNode) => void;
  isReplaced?: boolean;
};

function getBadgeSlot(isNew: boolean | undefined, isReplaced: boolean) {
  if (isNew) {
    return (
      <TrackingTag
        className="text-text/pending"
        dataTestId="new-node-tag"
        variant="pending"
      >
        {m.common_draft()}
      </TrackingTag>
    );
  }

  if (isReplaced) {
    return (
      <TrackingTag
        className="text-text/pending"
        dataTestId="replaced-node-tag"
        variant="pending"
      >
        {m.coInvestments_dataRoom_updated()}
      </TrackingTag>
    );
  }

  return null;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;

  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function FileTreeRow({
  node,
  depth,
  isCollapsed,
  onToggleCollapse,
  addItemMenu,
  onOpenFile,
  onOpenPdf,
  isReplaced,
}: FileTreeRowProps) {
  const formattedDate = formatDate(node.createdAt);
  const tooltipText = formattedDate
    ? m.coInvestments_dataRoom_fileTooltip({
        fileName: node.name,
        date: formattedDate,
      })
    : node.name;

  const tdRef = useRef<HTMLTableCellElement | null>(null);
  const {
    value: isDropTarget,
    setTrue: setIsDropTarget,
    setFalse: clearIsDropTarget,
  } = useBoolean(false);

  useEffect(() => {
    if (!tdRef.current) return;

    return dropTargetForExternal({
      element: tdRef.current,
      canDrop: containsFiles,
      onDragEnter: () => setIsDropTarget(),
      onDragLeave: () => clearIsDropTarget(),
      onDrop: () => clearIsDropTarget(),
    });
  }, [setIsDropTarget, clearIsDropTarget]);

  return (
    <DataRoomFileTreeCell
      actionsSlot={addItemMenu}
      badgeSlot={getBadgeSlot(node.isNew, isReplaced ?? false)}
      cellTestId={`data-room-tree-row-${node.id}`}
      className="md:sticky left-0 md:z-40 overflow-x-clip"
      collapseToggleTestId={`collapse-toggle-${node.id}`}
      contentClassName="px-1.5"
      depth={depth}
      isCollapsed={isCollapsed}
      isDropTarget={isDropTarget}
      node={node}
      onOpenFile={onOpenFile}
      onOpenPdf={onOpenPdf}
      onToggleCollapse={onToggleCollapse}
      tableCellRef={tdRef}
      title={tooltipText}
    />
  );
}
